import {
  FormControl,
  SearchPickerInput,
  useSearchPicker,
  FormErrorMessage,
  useMediaQuery,
} from '@wegroup/design-system';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FormOption } from '../../../../../../../../types/Form';
import { Option } from '../../../../../../../design-system/src/content/components/SearchPickerInput';
import { prefilledInputComponentStyling } from '../../../../../constants/InputComponentStyling';
import useFlowInputComponent from '../../../../../hooks/useFlowsInputComponent';
import { FlowsQuestion } from '../../../../../types/FlowsTypes';
import useEnumOptions from '../hooks/useEnumOptions';

interface Props {
  question: FlowsQuestion;
}

const IndexMultiselectInputComponent: React.FC<
  React.PropsWithChildren<Props>
> = ({ question }) => {
  const { t } = useTranslation();
  const {
    field: { value, onBlur },
    error,
    prefilled,
  } = useFlowInputComponent({ question });
  const [isMobile] = useMediaQuery('(max-width: 768px)', { ssr: false });

  const chosenValues = (value as number[] | undefined) || [];

  const enumOptions: FormOption[] = useEnumOptions(question);

  const options = useCallback(
    (val: string): Option[] => {
      return enumOptions.filter((o) =>
        o.label.toLowerCase().includes(val.toLowerCase()),
      );
    },
    [enumOptions],
  );

  const { componentProps } = useSearchPicker({
    searchFunction: options,
  });

  const handleOnBlur = (options: Option[]) => {
    onBlur({
      name: question.id,
      value: options.map(({ value }) => parseInt(value)),
    });
  };

  // Mobile-friendly styling overrides
  const mobileStyles = isMobile
    ? {
        sx: {
          // Mobile-specific input styling
          '& [data-test-id="General_SearchPickerList_List"]': {
            maxHeight: '70vh',
          },
          // Larger touch targets for mobile
          '& [data-test-id="COMPONENT_SEARCH-PICKER_SINGLE-OPTION"]': {
            minHeight: '48px',
            padding: '12px 16px',
            fontSize: '16px',
          },
          // Better spacing for checkboxes on mobile
          '& [data-test-id="COMPONENT_SEARCH-PICKER_SINGLE-OPTION"] > *:last-child':
            {
              width: '24px',
              height: '24px',
            },
          // Improve header button spacing on mobile
          '& button': {
            minHeight: '44px',
            padding: '12px 16px',
            fontSize: '16px',
          },
          // Mobile-friendly selected tag styling - using Chakra UI Tag selectors
          '& .chakra-tag': {
            maxWidth: '140px',
            fontSize: '13px',
            padding: '4px 8px',
            margin: '1px 4px 1px 0',
            height: '28px',
          },
          '& .chakra-tag__label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100px',
          },
          '& .chakra-tag__close-button': {
            width: '20px',
            height: '20px',
            marginLeft: '4px',
          },
          // Mobile input container adjustments
          '& > div:first-of-type': {
            minHeight: '54px',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            paddingTop: '6px',
            paddingBottom: '6px',
            gap: '2px',
          },
          ...(prefilled && prefilledInputComponentStyling),
        },
        // Constrain dropdown width on mobile - use viewport width minus some margin
        customWidth: 'calc(100vw - 32px)',
      }
    : prefilled && {
        sx: {
          ...prefilledInputComponentStyling,
          input: {
            bg: '#FFF9EA',
          },
        },
      };

  return (
    <FormControl
      isInvalid={!!error}
      w={isMobile ? 'calc(100vw - 32px)' : '100%'}
      maxW="100%"
    >
      <SearchPickerInput
        {...componentProps}
        isDisabled={question.rules.is_disabled}
        selectedOptions={enumOptions.filter(({ value: _value }) =>
          chosenValues.includes(parseInt(_value)),
        )}
        setSelectedOptions={handleOnBlur}
        boxShadow="base"
        border="1px"
        borderColor="gray.50"
        hideIcons
        placeholder={t('DEFAULT_MULTISELECT_OPTIES')}
        w="100%"
        {...mobileStyles}
        withSelectAll
      />
      <FormErrorMessage justifyContent="flex-end">
        {error?.message}
      </FormErrorMessage>
    </FormControl>
  );
};

export default IndexMultiselectInputComponent;
